import {
  parseCardId,
  RANK_ACE,
  RANK_JACK,
  RANK_KING,
  RANK_QUEEN,
  type CardId,
  type Seat,
  type Suit,
} from "@bing/engine-core";
import type { PasurSideTally } from "./types";

/** A played number card plus the cards it captures must total this. */
export const CAPTURE_SUM = 11;

/** Holding this many clubs or more (هفت خاج) scores the clubs points. With two sides sharing 13
 *  clubs, exactly one side always reaches it. */
export const CLUBS_MAJORITY_THRESHOLD = 7;

/**
 * The Iranian values (audit D12/D13): clubs ≥ 7 = 7, 10♦ = 3, 2♣ = 2, each Ace = 1, each Jack = 1
 * — 20 card points a deal — plus 5 for each standing سور. There is no «most cards» prize.
 */
export const POINTS = {
  CLUBS: 7,
  TEN_OF_DIAMONDS: 3,
  TWO_OF_CLUBS: 2,
  ACE: 1,
  JACK: 1,
  SUR: 5,
} as const;

/** The card points every deal distributes once all 52 cards are captured: 7 + 3 + 2 + 4 + 4. */
export const CARD_POINTS_PER_DEAL = 20;

/** The match is played to this many points (D15), per side. */
export const DEFAULT_TARGET_SCORE = 62;

/** پاسور is played by two sides — two players, or four in opposite partnerships (D19). */
export const SIDES = 2;

export const TEN_OF_DIAMONDS: CardId = "TD";
export const TWO_OF_CLUBS: CardId = "2C";

// Only 52 distinct ids, so memoize the parse — suit/rank are on the hot path.
const SUIT_CACHE = new Map<CardId, Suit>();
const RANK_CACHE = new Map<CardId, number>();

export function suitOf(id: CardId): Suit {
  let suit = SUIT_CACHE.get(id);
  if (suit === undefined) {
    suit = parseCardId(id).suit;
    SUIT_CACHE.set(id, suit);
  }
  return suit;
}

export function rankOf(id: CardId): number {
  let rank = RANK_CACHE.get(id);
  if (rank === undefined) {
    rank = parseCardId(id).rank;
    RANK_CACHE.set(id, rank);
  }
  return rank;
}

/** J, Q, K. */
export function isFaceCard(id: CardId): boolean {
  const rank = rankOf(id);
  return rank === RANK_JACK || rank === RANK_QUEEN || rank === RANK_KING;
}

/** The Jack (سرباز): captures every number card and Jack on the table, and never makes a سور. */
export function isJack(id: CardId): boolean {
  return rankOf(id) === RANK_JACK;
}

/** Ace and 2…10 — the "sum to 11" capturers/targets. */
export function isNumberCard(id: CardId): boolean {
  return !isFaceCard(id);
}

/** Capture value of a number card: Ace = 1, 2…10 = face value. Faces have none. */
export function pipValue(id: CardId): number {
  const rank = rankOf(id);
  return rank === RANK_ACE ? 1 : rank; // rank 2…10 map to themselves
}

export function isClub(id: CardId): boolean {
  return suitOf(id) === "clubs";
}

export function isAce(id: CardId): boolean {
  return rankOf(id) === RANK_ACE;
}

/** The side a seat plays for: seats 0 + 2 are side 0 and seats 1 + 3 side 1 (each seat its own
 *  side at 2 seats). */
export function sideOf(seat: Seat): number {
  return seat % SIDES;
}

/**
 * The table sizes پاسور is played at (D19): two players, or four in two opposite partnerships —
 * always two sides. Each size has its own room TYPES — literal `define()`s in `server.ts`, held to this
 * list by `pasur-two-seat.test.ts` — and the web's host picker offers it, so neither can name a table
 * the engine refuses to deal.
 */
export const PASUR_SEAT_COUNTS = [2, 4] as const;
export type PasurSeatCount = (typeof PASUR_SEAT_COUNTS)[number];

/** Only two sides exist: 2 seats (one each) or 4 (two opposite partnerships). */
export function assertPasurSeats(seats: number): void {
  if (!(PASUR_SEAT_COUNTS as readonly number[]).includes(seats)) {
    throw new Error(`Pasur is played by two sides: 2 or 4 players, got ${seats}`);
  }
}

/**
 * Every subset of `cards` whose pip values sum to exactly `target`. Never returns the empty subset
 * (`target` is always ≥1 in play). Each subset is a distinct set of card ids; two equal-value cards
 * of different suits yield two distinct subsets (their suits matter to scoring). Sorted-ascending +
 * pruned, so with a tiny `target` (≤10) this stays cheap even on a crowded table.
 */
export function subsetsSummingTo(cards: readonly CardId[], target: number): CardId[][] {
  const items = cards.map((id) => ({ id, v: pipValue(id) })).sort((a, b) => a.v - b.v);
  const out: CardId[][] = [];
  const chosen: CardId[] = [];

  const dfs = (start: number, remaining: number): void => {
    if (remaining === 0) {
      out.push([...chosen]);
      return;
    }
    for (let i = start; i < items.length; i++) {
      const item = items[i]!;
      if (item.v > remaining) break; // ascending → nothing later fits either
      chosen.push(item.id);
      dfs(i + 1, remaining - item.v);
      chosen.pop();
    }
  };

  dfs(0, target);
  return out;
}

/**
 * Every legal capture set for playing `card` onto `table` (each as a card-id list). An empty result
 * means the card can only be trailed.
 *  - D1/D2 a **Jack** has at most ONE option: every number card and every Jack on the table
 *    together (never a Queen or King). With none of those on the table it trails.
 *  - D3 a **Queen / King** takes one matching-rank card: one option per matching card.
 *  - D4 a **number card** takes ONE set of table number cards summing with it to 11: one option
 *    per such set, and the player chooses which.
 */
export function captureOptions(card: CardId, table: readonly CardId[]): CardId[][] {
  if (isJack(card)) {
    const sweep = table.filter((t) => isNumberCard(t) || isJack(t));
    return sweep.length > 0 ? [sweep] : [];
  }
  if (isFaceCard(card)) {
    const rank = rankOf(card);
    return table.filter((t) => rankOf(t) === rank).map((t) => [t]);
  }
  return subsetsSummingTo(table.filter(isNumberCard), CAPTURE_SUM - pipValue(card));
}

/** Order-insensitive key for a capture set, so callers can compare by identity. */
export function captureKey(ids: readonly CardId[]): string {
  return [...ids].sort().join(",");
}

/**
 * D6, card-91's refinement: the verdict on an opening table, applied after the first lay AND after
 * every replacement. ≥ 2 Jacks, ≥ 3 Queens or ≥ 3 Kings → reshuffle the whole deck; exactly one
 * Jack → bury it (back into the undealt deck at a seeded random place, D6b) and replace it from
 * the top; otherwise the table stands.
 */
export function openingTableVerdict(table: readonly CardId[]): "ok" | "bury" | "reshuffle" {
  let jacks = 0;
  let queens = 0;
  let kings = 0;
  for (const card of table) {
    const rank = rankOf(card);
    if (rank === RANK_JACK) jacks++;
    else if (rank === RANK_QUEEN) queens++;
    else if (rank === RANK_KING) kings++;
  }
  if (jacks >= 2 || queens >= 3 || kings >= 3) return "reshuffle";
  return jacks === 1 ? "bury" : "ok";
}

/**
 * Per-SIDE tallies of a deal's captured piles (`captured` is per seat; `surs` per side). Every
 * capture is face-up, so these are public facts at any moment of the deal.
 */
export function tallyDeal(
  captured: readonly (readonly CardId[])[],
  surs: readonly number[],
  seats: number,
): PasurSideTally[] {
  assertPasurSeats(seats);
  const tallies = Array.from({ length: SIDES }, (_unused, side) => ({
    cards: 0,
    clubs: 0,
    aces: 0,
    jacks: 0,
    twoOfClubs: false,
    tenOfDiamonds: false,
    surs: surs[side] ?? 0,
  }));
  for (let seat = 0; seat < seats; seat++) {
    const tally = tallies[sideOf(seat)]!;
    for (const card of captured[seat] ?? []) {
      tally.cards++;
      if (isClub(card)) tally.clubs++;
      if (isAce(card)) tally.aces++;
      if (isJack(card)) tally.jacks++;
      if (card === TWO_OF_CLUBS) tally.twoOfClubs = true;
      if (card === TEN_OF_DIAMONDS) tally.tenOfDiamonds = true;
    }
  }
  return tallies;
}

/** Per side: clubs 7 (at ≥ 7) + 2♣ 2 + 10♦ 3 + 1 an Ace + 1 a Jack + 5 a standing سور. */
export function scoreDeal(tallies: readonly PasurSideTally[]): number[] {
  return tallies.map(
    (t) =>
      (t.clubs >= CLUBS_MAJORITY_THRESHOLD ? POINTS.CLUBS : 0) +
      (t.twoOfClubs ? POINTS.TWO_OF_CLUBS : 0) +
      (t.tenOfDiamonds ? POINTS.TEN_OF_DIAMONDS : 0) +
      t.aces * POINTS.ACE +
      t.jacks * POINTS.JACK +
      t.surs * POINTS.SUR,
  );
}
